import { HttpErrorResponse } from '@angular/common/http';
import { computed } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ExamPhotosEndpoint, GetAiDermPhotoInfoEndpoint, SetAiDermPhotoInfoEndpoint } from '@pims-endpoints';
import { setError, setFulfilled, setPending, withRequestStatus } from '@shared-utils';
import { VisitDetailPhotos } from '@types-lib';
import { concatMap, pipe, switchMap, tap } from 'rxjs';
import { photoStoreInitialState } from './visit-detail-photos-store.state';

const PhotoStatuses = {
  PendingReview: VisitDetailPhotos.PhotoStatuses.PENDING_REVIEW,
  Approved: VisitDetailPhotos.PhotoStatuses.APPROVED,
  Rejected: VisitDetailPhotos.PhotoStatuses.REJECTED,
  Deleted: VisitDetailPhotos.PhotoStatuses.DELETED,
};

export const VisitDetailPhotoStore = signalStore(
  withState(photoStoreInitialState),
  withProps(() => ({
    _examPhotoEndpoint: new ExamPhotosEndpoint(),
    _getAiDermPhotoInfoEndpoint: new GetAiDermPhotoInfoEndpoint(),
    _setAiDermPhotoInfoEndpoint: new SetAiDermPhotoInfoEndpoint()
  })),
  withRequestStatus(),
  withComputed(({ examPhotos }) => ({
    examPhotosWithNoDuplicateFiles: computed(() => {
      const seen = new Set();
      return examPhotos().filter(photo => {
        const isDup = seen.has(photo.file?.id);
        seen.add(photo.file?.id);
        return !isDup && photo.file;
      });
    }),
  })),
  withMethods(store => {
    const setRejectedStatus = (photoId: number, status: boolean) => {
      patchState(store, {
        examPhotos: store.examPhotos().map(photo => (photo.id === photoId ? { ...photo, rejected: status } : photo)),
      });
    };

    const setExamPhotos = (photos: VisitDetailPhotos.Photo[]) => {
      patchState(store, { examPhotos: [...photos] });
    };

    const resetPhotosArrayDetails = () => {
      patchState(store, { examPhotos: [] });
    };

    const loadExamPhotos = rxMethod<number>(
      pipe(
        tap(() => patchState(store, setPending())),
        switchMap(visitId => store._examPhotoEndpoint.get(visitId)),
        tapResponse<VisitDetailPhotos.ExamPhotoResponse, HttpErrorResponse>({
          next: response => {
            const processedPhotos = response.photos.map(photo => ({
              ...photo,
              rejected: photo.file?.status === PhotoStatuses.Rejected,
              deleted: photo.file?.deleted ?? false,
              approved: photo.file?.status === PhotoStatuses.Approved,
            }));

            patchState(
              store,
              {
                examPhotos: processedPhotos,
                diagnosis: response.diagnosis ?? null,
              },
              setFulfilled(),
            );
          },
          error: error => {
            patchState(store, setError(error));
            console.error('[VisitDetailPhotoStore] HTTP Error:', error);
          },
        }),
      ),
    );

    const getAiDermResults = rxMethod<number>(
      pipe(
        tap(photoFileId => {
          patchState(store, {
            loadingAiDermMap: {
              ...store.loadingAiDermMap(),
              [photoFileId]: true,
            },
          });
        }),
        concatMap(photoFileId =>
          store._getAiDermPhotoInfoEndpoint.get(photoFileId).pipe(
            tapResponse<
              VisitDetailPhotos.AiDermCouldntDetectSkinError | VisitDetailPhotos.AiDermResponse,
              HttpErrorResponse
            >({
              next: response => {
                patchState(store, {
                  examPhotos: store.examPhotos().map(photo =>
                    photo.file.id === photoFileId
                      ? {
                          ...photo,
                          aiDermInfo:
                            'error_message' in response
                              ? {
                                  file_id: photoFileId,
                                  error: 'AI could not generate a result. Please reject and request a new photo.',
                                }
                              : response,
                        }
                      : photo,
                  ),
                  loadingAiDermMap: {
                    ...store.loadingAiDermMap(),
                    [photoFileId]: false,
                  },
                });
              },
              error: error => {
                const errorMessage = error.status === 500 
                  ? 'Error getting results, please try again later'
                  : 'AI could not generate a result. Please reject and request a new photo.';
                
                patchState(store, {
                  examPhotos: store.examPhotos().map(photo =>
                    photo.file.id === photoFileId
                      ? {
                          ...photo,
                          aiDermInfo: {
                            file_id: photoFileId,
                            error: errorMessage,
                          },
                        }
                      : photo,
                  ),
                  loadingAiDermMap: {
                    ...store.loadingAiDermMap(),
                    [photoFileId]: false,
                  },
                });
                console.error('[VisitDetailPhotoStore] AI Derm GET Error:', error);
              },
            }),
          ),
        ),
      ),
    );

    const setAiDermResults = rxMethod<VisitDetailPhotos.AiDermPostRequest>(
      pipe(
        tap(request => {
          patchState(store, {
            loadingAiDermMap: {
              ...store.loadingAiDermMap(),
              [request.file_id]: true,
            },
          });
        }),
        concatMap(request =>
          store._setAiDermPhotoInfoEndpoint.post(request).pipe(
            tapResponse<
              VisitDetailPhotos.AiDermCouldntDetectSkinError | VisitDetailPhotos.AiDermResponse,
              HttpErrorResponse
            >({
              next: response => {
                patchState(store, {
                  examPhotos: store.examPhotos().map(photo =>
                    photo.file.id === request.file_id
                      ? {
                          ...photo,
                          aiDermInfo:
                            'error_message' in response
                              ? {
                                  file_id: request.file_id,
                                  error: 'AI could not generate a result. Please reject and request a new photo.',
                                }
                              : response,
                        }
                      : photo,
                  ),
                  loadingAiDermMap: {
                    ...store.loadingAiDermMap(),
                    [request.file_id]: false,
                  },
                });
              },
              error: error => {
                const errorMessage = error.status === 500 
                  ? 'Error getting results, please try again later'
                  : 'AI could not generate a result. Please reject and request a new photo.';
                
                patchState(store, {
                  examPhotos: store.examPhotos().map(photo =>
                    photo.file.id === request.file_id
                      ? {
                          ...photo,
                          aiDermInfo: {
                            file_id: request.file_id,
                            error: errorMessage,
                          },
                        }
                      : photo,
                  ),
                  loadingAiDermMap: {
                    ...store.loadingAiDermMap(),
                    [request.file_id]: false,
                  },
                });
                console.error('[VisitDetailPhotoStore] AI Derm POST Error:', error);
              },
            }),
          ),
        ),
      ),
    );

    return {
      setRejectedStatus,
      setExamPhotos,
      resetPhotosArrayDetails,
      loadExamPhotos,
      getAiDermResults,
      setAiDermResults,
    };
  }),
  withHooks({
    onDestroy(store) {
      store.loadExamPhotos.destroy();
      store.getAiDermResults.destroy();
      store.setAiDermResults.destroy();
    },
  }),
);
